import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Check, Zap, Crown, Star, ArrowRight, Loader2, ChevronLeft, Lock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useSubscription } from '../context/SubscriptionContext';
import { useSEO } from '../hooks/useSEO';
import { API_BASE_URL } from '../config/api';
import { useTranslation } from 'react-i18next';

const PLANS = [
  {
    id: 'free',
    name: 'Free',
    monthlyPrice: 0,
    yearlyPrice: 0,
    yearlyDiscount: 0,
    subtitle: 'Basic Tracking',
    color: 'neutral',
    icon: Star,
    features: [
      '1 Week Free Pro Trial',
      'Manual Trades (max. 5/month)',
      'Basic Trade Journal',
      'TradingView Chart',
    ],
    locked: [],
    cta: 'Current Plan',
    isFree: true,
  },
  {
    id: 'pro',
    name: 'Pro',
    monthlyPrice: 99000,
    yearlyPrice: 949000,
    yearlyDiscount: 20,
    subtitle: 'For Active Traders',
    color: 'red',
    icon: Zap,
    popular: true,
    features: [
      'Auto-Sync from all CEXs',
      'Unlimited Manual Trades',
      'Calendar Daily Journal',
      'AI Assistant',
      'Portfolio Analytics',
    ],
    cta: 'Upgrade to Pro',
  },
  {
    id: 'premium',
    name: 'Premium',
    monthlyPrice: 179000,
    yearlyPrice: 1499000,
    yearlyDiscount: 22,
    subtitle: 'For Advanced Traders',
    color: 'purple',
    icon: Crown,
    features: [
      'All Pro Features',
      'Catat Crypto Score Dashboard',
      'Dev Picks',
      'Crypto News',
      'Top 50 Trending',
      'Top Traders',
      'Early Access to New Features',
      'Priority Support',
    ],
    cta: 'Go Premium',
  },
];

const formatIDR = (n: number) =>
  n === 0 ? 'Free' : `IDR ${n.toLocaleString('en-US')}`;

const PricingPage: React.FC = () => {
  useSEO({
    title: 'Subscription Pricing Plans - Pro & Premium',
    description: 'Explore CatatCrypto subscription plans. Upgrade to Pro or Premium to unlock auto-sync, AI Trade Assistant, and premium dashboards.',
    keywords: 'pricing, subscription plans, pro trading journal, premium portfolio tracker',
    canonical: 'https://catatcrypto.com/pricing',
    ogUrl: 'https://catatcrypto.com/pricing'
  });

  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const { effectivePlan, subscription, refresh } = useSubscription();
  const [cycle, setCycle] = useState<'monthly' | 'yearly'>('monthly');
  const [loadingPlan, setLoadingPlan] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [waitingPaymentPlan, setWaitingPaymentPlan] = useState<string | null>(null);
  const [paymentSuccess, setPaymentSuccess] = useState<boolean>(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Poll for subscription changes when waiting for payment
  useEffect(() => {
    if (!waitingPaymentPlan) return;

    const intervalId = setInterval(async () => {
      await refresh();
    }, 3000);

    return () => clearInterval(intervalId);
  }, [waitingPaymentPlan, refresh]);

  // Monitor plan changes to detect payment success
  useEffect(() => {
    if (waitingPaymentPlan && subscription?.plan === waitingPaymentPlan) {
      setWaitingPaymentPlan(null);
      setPaymentSuccess(true);

      const hasCex = localStorage.getItem('has_cex_accounts') === 'true';
      if (!hasCex) {
        // Unfinished onboarding: direct them to step 5 (broker selection) after a short delay
        localStorage.setItem('app_step', '5');
        const timer = setTimeout(() => {
          navigate('/onboarding');
        }, 1500);
        return () => clearTimeout(timer);
      } else {
        // Auto-hide success message after 6 seconds
        const timer = setTimeout(() => {
          setPaymentSuccess(false);
        }, 6000);
        return () => clearTimeout(timer);
      }
    }
  }, [subscription?.plan, waitingPaymentPlan, navigate]);

  const handleSubscribe = async (planId: string) => {
    const currentPlan = subscription?.plan;
    const currentCycle = subscription?.billing_cycle;
    const hasPaidActiveSub = currentPlan !== undefined && currentPlan !== 'free' && currentPlan !== 'expired' && subscription?.status !== 'trial';

    if (hasPaidActiveSub) {
      if (currentPlan === 'premium' && planId === 'pro') {
        const isId = i18n.language === 'id';
        setError(isId 
          ? "Lu ga bisa downgrade langganan lu, lagian buat apa juga? 🤨" 
          : "You cannot downgrade your subscription, because why would you? 🤨"
        );
        return;
      }
      if (currentPlan === planId && currentCycle === 'yearly' && cycle === 'monthly') {
        const isId = i18n.language === 'id';
        setError(isId 
          ? "Anda tidak bisa mengubah siklus tahunan ke bulanan secara langsung. Hubungi tim support jika butuh bantuan." 
          : "You cannot change from yearly to monthly cycle directly. Contact support if you need help."
        );
        return;
      }
    }

    const isPaidActive = hasPaidActiveSub && planId === currentPlan && cycle === currentCycle;
    if (planId === 'free' || isPaidActive) return;
    setLoadingPlan(planId);
    setError('');

    try {
      const token = localStorage.getItem('auth_token');
      const savedRefCode = localStorage.getItem('prefilled_ref_code');
      const res = await fetch(`${API_BASE_URL}/api/subscription/create-invoice`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ 
          plan: planId, 
          cycle
        }),
      });

      const data = await res.json();

      if (res.status === 409 && data.payment_url) {
        // Existing pending invoice
        window.open(data.payment_url, '_blank');
        setWaitingPaymentPlan(planId);
        return;
      }

      if (!res.ok) {
        setError(data.message || 'Failed to create invoice. Try again.');
        return;
      }

      if (data.payment_url) {
        window.open(data.payment_url, '_blank');
        await refresh();
        setWaitingPaymentPlan(planId);
      }
    } catch (err) {
      setError('Connection failed. Make sure the server is running.');
    } finally {
      setLoadingPlan(null);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Back button */}
      <div className="p-6">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-neutral-400 hover:text-white transition-colors text-sm font-bold"
        >
          <ChevronLeft size={18} /> Back
        </button>
      </div>

      <div className="max-w-5xl mx-auto px-6 pb-20">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-red-600/10 border border-red-600/20 rounded-full mb-4">
            <div className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
            <span className="text-[10px] font-black uppercase tracking-widest text-red-400">Pricing</span>
          </div>
          <h1 className="text-4xl lg:text-5xl font-black tracking-tight mb-4">
            Choose the Right{' '}
            <span className="bg-gradient-to-r from-red-400 to-red-600 bg-clip-text text-transparent">
              Plan
            </span>
          </h1>
          <p className="text-neutral-400 text-lg max-w-xl mx-auto">
            Scale your trading journal as your profits grow.
          </p>

          {/* Billing toggle */}
          <div className="flex items-center justify-center gap-3 mt-8">
            <span className={`text-sm font-bold transition-colors ${cycle === 'monthly' ? 'text-white' : 'text-neutral-500'}`}>
              Monthly
            </span>
            <button
              onClick={() => setCycle(c => c === 'monthly' ? 'yearly' : 'monthly')}
              className={`relative w-12 h-6 rounded-full transition-colors ${cycle === 'yearly' ? 'bg-red-600' : 'bg-neutral-700'}`}
            >
              <div className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-all ${cycle === 'yearly' ? 'left-7' : 'left-1'}`} />
            </button>
            <span className={`text-sm font-bold transition-colors ${cycle === 'yearly' ? 'text-white' : 'text-neutral-500'}`}>
              Yearly
            </span>
          </div>
        </motion.div>

        {/* Error */}
        {error && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="mb-6 p-4 bg-red-900/20 border border-red-900/50 rounded-xl text-sm text-red-400 text-center"
          >
            {error}
          </motion.div>
        )}

        {/* Plan cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {PLANS.map((plan, i) => {
            const price = cycle === 'yearly' ? plan.yearlyPrice : plan.monthlyPrice;
            const originalYearlyPrice = plan.monthlyPrice * 12;
            const showYearlyDiscount = cycle === 'yearly' && !plan.isFree;
            const currentPlan = subscription?.plan;
            const currentCycle = subscription?.billing_cycle;
            const hasPaidActiveSub = currentPlan !== undefined && currentPlan !== 'free' && currentPlan !== 'expired' && subscription?.status !== 'trial';

            const isCurrentPlan = effectivePlan === plan.id;
            const isPaidActive = hasPaidActiveSub && currentPlan === plan.id && currentCycle === cycle;
            const isTrial = isCurrentPlan && !isPaidActive;
            
            const isPlanDowngrade = hasPaidActiveSub && currentPlan === 'premium' && plan.id === 'pro';
            const isCycleDowngrade = hasPaidActiveSub && currentPlan === plan.id && currentCycle === 'yearly' && cycle === 'monthly';
            const isDowngradeLocked = isPlanDowngrade || isCycleDowngrade;
            const isLoading = loadingPlan === plan.id;
            const Icon = plan.icon;

            const colorMap = {
              neutral: { ring: '', badge: 'bg-neutral-800 text-neutral-400', btn: 'bg-neutral-800 hover:bg-neutral-700 text-white', icon: 'bg-neutral-800', check: 'text-neutral-500', glow: '' },
              red: { ring: 'ring-2 ring-red-600/60', badge: 'bg-red-600 text-white', btn: 'bg-red-600 hover:bg-red-500 text-white shadow-lg shadow-red-900/40', icon: 'bg-red-600', check: 'text-red-500', glow: 'shadow-red-900/20' },
              purple: { ring: 'ring-2 ring-purple-600/60', badge: 'bg-purple-600 text-white', btn: 'bg-purple-600 hover:bg-purple-500 text-white shadow-lg shadow-purple-900/40', icon: 'bg-purple-600', check: 'text-purple-500', glow: 'shadow-purple-900/20' },
            } as const;

            const colors = colorMap[plan.color as keyof typeof colorMap] ?? colorMap.neutral;

            return (
              <motion.div
                key={plan.id}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                className={`relative bg-[#0D0D0D] border border-neutral-800 rounded-3xl p-6 flex flex-col ${plan.popular ? colors.ring : ''} ${plan.popular ? 'shadow-2xl ' + colors.glow : ''}`}
              >
                {plan.popular && (
                  <div className={`absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest ${colors.badge}`}>
                    Most Popular
                  </div>
                )}

                {isCurrentPlan && (
                  <div className="absolute -top-3 right-4 px-3 py-1 bg-green-600 rounded-full text-[10px] font-black uppercase tracking-widest text-white">
                    {isTrial ? 'Trial active' : 'Your Plan'}
                  </div>
                )}

                <div className="mb-6">
                  <div className={`w-10 h-10 rounded-2xl flex items-center justify-center mb-4 ${colors.icon}`}>
                    <Icon size={20} className="text-white" />
                  </div>
                  <h2 className="text-xl font-black text-white">{plan.name}</h2>
                  <p className="text-xs text-neutral-500 mt-0.5">{plan.subtitle}</p>
                </div>

                <div className="mb-6">
                  {showYearlyDiscount && (
                    <div className="mb-2 flex items-center gap-2">
                      <span className="text-sm font-bold text-neutral-500 line-through decoration-red-500/80 decoration-2">
                        {formatIDR(originalYearlyPrice)}
                      </span>
                      <span className={`rounded-full px-2 py-0.5 text-[10px] font-black uppercase tracking-widest ${plan.color === 'purple' ? 'bg-purple-600/20 text-purple-300 border border-purple-500/30' : 'bg-red-600/20 text-red-300 border border-red-500/30'}`}>
                        Save {plan.yearlyDiscount}%
                      </span>
                    </div>
                  )}
                  <div className="flex items-end gap-1.5">
                    <span className="text-3xl font-black text-white">
                      {plan.isFree ? 'Free' : formatIDR(price)}
                    </span>
                    {!plan.isFree && (
                      <span className="pb-1 text-sm text-neutral-500">{cycle === 'yearly' ? '/year' : '/month'}</span>
                    )}
                  </div>
                  {cycle === 'yearly' && !plan.isFree && (
                    <p className="text-[10px] text-neutral-500 mt-1">
                      Equivalent to {formatIDR(Math.round(price / 12))}/month
                    </p>
                  )}
                </div>

                <div className="space-y-3 flex-1 mb-8">
                  {plan.features.map((f) => (
                    <div key={f} className="flex items-start gap-3">
                      <div className={`w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 bg-opacity-20 border border-opacity-30 ${colors.check === 'text-red-500' ? 'bg-red-600/20 border-red-600/30' : colors.check === 'text-purple-500' ? 'bg-purple-600/20 border-purple-600/30' : 'bg-neutral-700/50 border-neutral-700'}`}>
                        <Check size={9} className={colors.check} />
                      </div>
                      <span className="text-sm text-neutral-300 leading-tight">{f}</span>
                    </div>
                  ))}
                </div>

                 <button
                  onClick={() => handleSubscribe(plan.id)}
                  disabled={isLoading || isPaidActive || plan.isFree}
                  className={`w-full py-3.5 rounded-2xl font-black text-sm flex items-center justify-center gap-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
                    isPaidActive
                      ? 'bg-green-600/20 border border-green-600/40 text-green-400'
                      : isDowngradeLocked
                      ? 'bg-neutral-900 border border-neutral-800 text-neutral-500 hover:bg-neutral-800/50'
                      : plan.isFree
                      ? 'bg-neutral-800 text-neutral-500 cursor-default'
                      : colors.btn
                  }`}
                >
                  {isLoading ? (
                    <><Loader2 size={16} className="animate-spin" /> Creating Invoice...</>
                  ) : isPaidActive ? (
                    <><Check size={16} /> {plan.cta}</>
                  ) : isDowngradeLocked ? (
                    <><Lock size={16} /> {
                      isPlanDowngrade
                        ? (i18n.language === 'id' ? 'Downgrade Terkunci' : 'Downgrade Locked')
                        : (i18n.language === 'id' ? 'Siklus Terkunci' : 'Cycle Locked')
                    }</>
                  ) : (
                    <>{plan.cta} <ArrowRight size={16} /></>
                  )}
                </button>
              </motion.div>
            );
          })}
        </div>

        {/* FAQ / note */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="mt-12 text-center space-y-2"
        >
          <p className="text-neutral-600 text-sm">
            All prices in IDR · Secure payments
          </p>
          <p className="text-neutral-600 text-sm">
            Cancel anytime · Access remains active until the end of the billing period
          </p>
        </motion.div>
      </div>

      {/* Payment Success Toast */}
      <AnimatePresence>
        {paymentSuccess && (
          <motion.div
            initial={{ opacity: 0, y: -50, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -20, scale: 0.95 }}
            className="fixed top-8 right-8 z-[110] max-w-md w-full px-4"
          >
            <div className="bg-gradient-to-r from-green-950/90 to-green-900/90 backdrop-blur-xl border border-green-500/30 rounded-2xl shadow-2xl overflow-hidden p-5 flex items-start gap-4">
              <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center shrink-0">
                <Check size={20} className="text-white" />
              </div>
              <div className="text-left">
                <h4 className="text-sm font-black text-white">Pembayaran Berhasil!</h4>
                {localStorage.getItem('has_cex_accounts') === 'true' ? (
                  <>
                    <p className="text-xs text-green-200/80 mt-1 leading-relaxed">
                      Langganan Anda telah diperbarui menjadi plan <span className="text-white font-bold capitalize">{effectivePlan}</span>. Silakan kembali ke dashboard untuk menikmati fitur premium.
                    </p>
                    <button
                      onClick={() => navigate('/dashboard')}
                      className="mt-3 px-4 py-2 bg-green-500 hover:bg-green-400 text-[#052e16] text-[10px] font-black uppercase tracking-wider rounded-xl transition-all"
                    >
                      Ke Dashboard
                    </button>
                  </>
                ) : (
                  <>
                    <p className="text-xs text-green-200/80 mt-1 leading-relaxed">
                      Langganan Anda telah diperbarui menjadi plan <span className="text-white font-bold capitalize">{effectivePlan}</span>. Mengalihkan untuk menghubungkan broker Anda...
                    </p>
                    <button
                      onClick={() => {
                        localStorage.setItem('app_step', '5');
                        navigate('/onboarding');
                      }}
                      className="mt-3 px-4 py-2 bg-green-500 hover:bg-green-400 text-[#052e16] text-[10px] font-black uppercase tracking-wider rounded-xl transition-all"
                    >
                      Hubungkan Broker
                    </button>
                  </>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Waiting for Payment Dialog */}
      <AnimatePresence>
        {waitingPaymentPlan && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[100]"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 10 }}
              className="fixed inset-0 z-[101] flex items-center justify-center p-4"
            >
              <div className="bg-[#0D0D0D] border border-neutral-800 rounded-3xl p-8 max-w-md w-full text-center space-y-6 shadow-2xl relative overflow-hidden">
                <div className="absolute -top-12 -left-12 w-40 h-40 bg-red-600 rounded-full blur-[90px] opacity-10" />
                
                <div className="w-16 h-16 rounded-2xl bg-red-600/10 border border-red-600/20 flex items-center justify-center mx-auto text-red-500">
                  <Loader2 size={32} className="animate-spin" />
                </div>
                
                <div className="space-y-2">
                  <h3 className="text-lg font-black text-white uppercase tracking-tight">Menunggu Pembayaran</h3>
                  <p className="text-xs text-neutral-400 leading-relaxed">
                    Selesaikan pembayaran Anda untuk plan <span className="text-white font-bold capitalize">{waitingPaymentPlan}</span> di tab pembayaran yang baru dibuka.
                  </p>
                  <p className="text-[10px] text-neutral-500 italic">
                    Halaman ini akan otomatis diperbarui dan beralih ke Dashboard setelah pembayaran diverifikasi.
                  </p>
                </div>
                
                <div className="pt-2 flex flex-col gap-2">
                  <button
                    onClick={async () => {
                      await refresh();
                    }}
                    className="w-full py-3 bg-red-600 hover:bg-red-500 text-white text-xs font-black uppercase tracking-widest rounded-xl transition-all shadow-lg shadow-red-900/10"
                  >
                    Cek Status Pembayaran
                  </button>
                  <button
                    onClick={() => setWaitingPaymentPlan(null)}
                    className="w-full py-2.5 text-neutral-500 hover:text-neutral-300 text-xs font-bold transition-all"
                  >
                    Tutup Dialog
                  </button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default PricingPage;
